
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User, UserCircle, LogIn, Key, Mail, Edit } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import horsebeatLogo from "@/assets/logo-horsebeat.png";
import { validateUsername } from "@/utils/security";
import { trackAuth, trackLoginFailed, trackRegistrationFailed } from "@/utils/analytics";
import { captureError, addBreadcrumb, setSentryUser } from "@/utils/sentry";
import { safeStorage } from "@/utils/safeStorage";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const { user, isAuthenticated } = useAuth();
  const { profile, updateUsername } = useUserProfile();
  const { toast } = useToast();
  const navigate = useNavigate();
 
  // Persist intended redirect on mount (if coming from /fantasy etc.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect && redirect.startsWith('/')) {
      safeStorage.setItem('postAuthRedirect', redirect);
    }
  }, []);

  useEffect(() => {
    // Redirect if already authenticated and not on specific pages
    if (
      isAuthenticated &&
      !window.location.search.includes('change-password') &&
      !window.location.search.includes('edit-profile')
    ) {
      const params = new URLSearchParams(window.location.search);
      const paramRedirect = params.get('redirect');
      const storedRedirect = safeStorage.getItem('postAuthRedirect');
      const target = (storedRedirect && storedRedirect.startsWith('/'))
        ? storedRedirect
        : (paramRedirect && paramRedirect.startsWith('/') ? paramRedirect : '/');
      navigate(target);
      if (storedRedirect) safeStorage.removeItem('postAuthRedirect');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Set current username when profile loads
    if (profile?.username) {
      setEditUsername(profile.username);
    }
  }, [profile]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        trackLoginFailed(error.message, 'email');
        captureError(error, { email, method: 'email' });
        toast({
          title: "Chyba při přihlášení",
          description: error.message,
          variant: "destructive",
        });
      } else {
        trackAuth('login', 'email');
        addBreadcrumb('User logged in', { email, method: 'email' });
        toast({
          title: "Úspěch",
          description: "Byli jste úspěšně přihlášeni!",
        });
      }
    } catch (error: any) {
      console.error("Login error:", error);
      trackLoginFailed(error.message || 'Unknown error', 'email');
      captureError(error, { email, method: 'email' });
      toast({
        title: "Chyba",
        description: "Nepodařilo se přihlásit",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    const usernameValidation = validateUsername(username);
    if (!usernameValidation.isValid) {
      toast({
        title: "Chyba při registraci",
        description: usernameValidation.error,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Check if username already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        toast({
          title: "Chyba při registraci",
          description: "Nepodařilo se ověřit dostupnost uživatelského jména",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (existingUser) {
        toast({
          title: "Chyba při registraci",
          description: "Uživatelské jméno již existuje",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: username,
          }
        }
      });

      if (error) {
        trackRegistrationFailed(error.message, 'email');
        captureError(error, { email, username, method: 'email' });
        toast({
          title: "Chyba při registraci",
          description: error.message,
          variant: "destructive",
        });
      } else {
        trackAuth('register', 'email');
        addBreadcrumb('User registered', { email, username, method: 'email' });
        toast({
          title: "Registrace úspěšná",
          description: "Zkontrolujte svůj email pro potvrzení účtu",
        });
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      trackRegistrationFailed(error.message || 'Unknown error', 'email');
      captureError(error, { email, username, method: 'email' });
      toast({
        title: "Chyba",
        description: "Nepodařilo se registrovat",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const target = redirect && redirect.startsWith('/') ? redirect : '/';
      // Persist intended path to survive provider redirects or URL config mismatches
      safeStorage.setItem('postAuthRedirect', target);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth?redirect=${encodeURIComponent(target)}`
        }
      });

      if (error) {
        trackLoginFailed(error.message, 'google');
        captureError(error, { method: 'google' });
        toast({
          title: "Chyba při přihlášení přes Google",
          description: error.message,
          variant: "destructive",
        });
      } else {
        trackAuth('login', 'google');
        addBreadcrumb('Google OAuth initiated');
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se přihlásit přes Google",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Chyba",
        description: "Hesla se neshodují",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Chyba",
        description: "Heslo musí mít alespoň 6 znaků",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast({
          title: "Chyba při změně hesla",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Úspěch",
          description: "Heslo bylo úspěšně změněno",
        });
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      console.error("Password change error:", error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se změnit heslo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      toast({
        title: "Chyba",
        description: "Zadejte email pro obnovení hesla",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth?change-password=true`
      });

      if (error) {
        toast({
          title: "Chyba při obnovení hesla",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email odeslán",
          description: "Zkontrolujte svůj email pro obnovení hesla",
        });
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se odeslat email pro obnovení hesla",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameUpdate = async () => {
    const usernameValidation = validateUsername(editUsername);
    if (!usernameValidation.isValid) {
      toast({
        title: "Chyba",
        description: usernameValidation.error,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await updateUsername(editUsername);

      if (error) {
        toast({
          title: "Chyba při změně jména",
          description: error,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Úspěch",
          description: "Uživatelské jméno bylo úspěšně změněno",
        });
      }
    } catch (error) {
      console.error("Username update error:", error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se změnit uživatelské jméno",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-white/70 backdrop-blur-sm border-border">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img src={horsebeatLogo} alt="Horsebeat Logo" className="h-16 w-auto" />
            </div>
            <p className="text-muted-foreground">{isAuthenticated ? "Spravujte svůj účet" : "Přihlaste se a tipujte své favority!"}</p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={
              isAuthenticated 
                ? (window.location.search.includes('edit-profile') ? "edit-profile" : "change-password")
                : "login"
            } className="w-full">
              <TabsList className={`grid w-full ${isAuthenticated ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {!isAuthenticated && (
                  <>
                    <TabsTrigger value="login">
                      <LogIn className="h-4 w-4 mr-2" />
                      Přihlášení
                    </TabsTrigger>
                    <TabsTrigger value="signup">
                      <User className="h-4 w-4 mr-2" />
                      Registrace
                    </TabsTrigger>
                    <TabsTrigger value="reset">
                      <Mail className="h-4 w-4 mr-2" />
                      Zapomenuté heslo
                    </TabsTrigger>
                  </>
                )}
                {isAuthenticated && (
                  <>
                    <TabsTrigger value="edit-profile">
                      <Edit className="h-4 w-4 mr-2" />
                      Upravit profil
                    </TabsTrigger>
                    <TabsTrigger value="change-password">
                      <Key className="h-4 w-4 mr-2" />
                      Změna hesla
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
              
              {!isAuthenticated && (
                <>
                  <TabsContent value="login" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="vas@email.cz"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Heslo</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button 
                      onClick={handleLogin} 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? "Přihlašuji..." : "Přihlásit se"}
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">nebo</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleGoogleLogin} 
                      variant="outline"
                      className="w-full"
                      disabled={loading}
                    >
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      {loading ? "Přihlašuji..." : "Přihlásit se přes Google"}
                    </Button>
                  </TabsContent>
              
                  <TabsContent value="signup" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-username">Uživatelské jméno</Label>
                      <Input
                        id="signup-username"
                        type="text"
                        placeholder="VaseJmeno"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        maxLength={15}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="vas@email.cz"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Heslo</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button 
                      onClick={handleSignUp} 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? "Registruji..." : "Registrovat se"}
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="reset" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="vas@email.cz"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                      />
                    </div>
                    <Button 
                      onClick={handlePasswordReset} 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? "Odesílám..." : "Odeslat email pro obnovení hesla"}
                    </Button>
                  </TabsContent>
                </>
              )}
              
              {isAuthenticated && (
                <>
                  <TabsContent value="edit-profile" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-username">Uživatelské jméno</Label>
                      <Input
                        id="edit-username"
                        type="text"
                        placeholder="VaseJmeno"
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        maxLength={15}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={user?.email || ""}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-sm text-gray-600">
                        Email nelze změnit
                      </p>
                    </div>
                    <Button 
                      onClick={handleUsernameUpdate} 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? "Ukládám..." : "Uložit změny"}
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="change-password" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">Nové heslo</Label>
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Potvrdit nové heslo</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <Button 
                      onClick={handlePasswordChange} 
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? "Měním heslo..." : "Změnit heslo"}
                    </Button>
                  </TabsContent>
                </>
              )}
            </Tabs>
            
            <div className="mt-6 text-center">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="text-muted-foreground"
              >
                Zpět na hlavní stránku
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
